import React, { memo, useCallback, useMemo } from 'react';
import cx from 'clsx';
import { RichTextEditor } from '@debank/content-editor';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactGA4 from 'react-ga4';
import { UserAvatar } from '@/components';
import { useTheme, useUserInfo, useCurrentAddressInfo, getGaUserType } from '@/hooks';
import { useLocale } from '@/utils/i18n';
import { suicideToast } from '@/components/SystemErrorToast';

import { useFeedCreate } from '../../hooks/detail';
import style from './index.module.scss';
import { useUserTrial } from '@/hooks/trial';

type HeaderEditorProps = {
  className?: string;
};

export const HeaderEditor = memo((props: HeaderEditorProps) => {
  const { className } = props;
  const { isDark } = useTheme();
  const [currentLocale] = useLocale();
  const history = useHistory();
  const { t } = useTranslation();

  const { address, userInfo } = useCurrentAddressInfo();
  const { mutateAsync: postContent } = useFeedCreate();
  const { data: currentUserInfo } = useUserInfo(address);
  const { data: trialInfo } = useUserTrial('POST');

  const handlePostContent = useCallback(
    async data => {
      if (currentUserInfo?.is_suicide) {
        suicideToast();
        return;
      }
      const res = await postContent(data);
      if (res?.article.id) {
        ReactGA4.event({
          category: 'New Post',
          action: `New Post_Direct_${getGaUserType()}`,
        });
        history.push(`/stream/${res?.article.id}`);
      }
    },
    [currentUserInfo?.is_suicide, history, postContent],
  );

  const isSybil = useMemo(
    () => Boolean(currentUserInfo?.desc?.is_spam),
    [currentUserInfo?.desc?.is_spam],
  );

  const disabledBanner = useMemo(() => {
    const isTrialReached = trialInfo && !trialInfo?.is_active;
    return Boolean(isSybil || isTrialReached);
  }, [isSybil, trialInfo]);

  const handleDisableClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  return (
    <div className={cx(style.container, className)}>
      {disabledBanner ? (
        <div className={style.disableBanner} onClick={handleDisableClick}>
          {isSybil ? (
            t('MintIdBanner.1kclh83')
          ) : (
            <div>
              {t('MintIdBanner.tip1')}
              {t('MintIdBanner.tip2')}
            </div>
          )}
        </div>
      ) : null}
      <UserAvatar data={userInfo?.desc} className={style.avatar} size={36} />
      <RichTextEditor
        language={currentLocale}
        onConfirm={handlePostContent}
        maxLength={600}
        isDarkMode={isDark}
        footer={false}
        wrapperClassName={style.editorWrapper}
        editorClassName={style.editor}
        cardClassName={style.editorCard}
        placeholderClassName={style.placeHolder}
        toolbarClassName={style.toolbar}
        postInToolbar
        toolbarPostBtnClassName={style.postBtn}
      />
    </div>
  );
});
